import {
  HttpError,
  isHttpError,
  panic
} from "./chunk-KKRZEVOT.js";

// src/utils/requestConfig.ts
var mergeHeaders = (...headers) => {
  const mergedHeaders = new Headers();
  for (const currentHeaders of headers) {
    if (!currentHeaders) {
      continue;
    }
    for (const [key, value] of Object.entries(currentHeaders)) {
      mergedHeaders.set(key, value);
    }
  }
  return Object.fromEntries(mergedHeaders.entries());
};
var mergeApiConfig = (baseConfig, overrideConfig) => {
  return {
    ...baseConfig,
    ...overrideConfig,
    headers: mergeHeaders(baseConfig.headers, overrideConfig.headers)
  };
};

// package.json
var package_default = {
  name: "@ev-tech/search-modules-api",
  version: "0.0.1-local",
  type: "module",
  module: "build/index.js",
  typings: "build/index.d.ts",
  files: [
    "build"
  ],
  exports: {
    ".": {
      types: "./build/index.d.ts",
      default: "./build/index.js"
    }
  },
  sideEffects: false,
  license: "MIT",
  scripts: {
    build: "export NODE_OPTIONS=--max-old-space-size-percentage=90; tsx ./scripts/build.ts",
    dev: "tsup --watch",
    test: "vitest run -w",
    "test:ci": "vitest run --coverage --reporter=junit",
    "test:ui": "BROWSER=open vitest --coverage --ui",
    "generate:api": "tsx ./scripts/openapi/generate.ts --api search-api",
    "generate:bff": "tsx ./scripts/openapi/generate.ts --api search-bff",
    "generate:ai": "tsx ./scripts/openapi/generate.ts --api ai-chat-bot",
    "type-check": "pnpm -w run ts7:tsc -p packages/api/tsconfig.json --noEmit",
    lint: "cd ../../ && oxlint --fix packages/api",
    "packages:pack": "pnpm pack --out ../../dist/release-artifacts/api/tarball.tgz",
    clean: "rm -rf .turbo && rm -rf node_modules && rm -rf build",
    "packages:version": "pnpm version $RELEASE_VERSION --no-git-tag-version --no-git-checks",
    "install-secrets": "tsx ../../scripts/install-secrets.ts --app api"
  },
  devDependencies: {
    "@faker-js/faker": "10.3.0",
    "@types/js-sha1": "^0.6.3",
    "@types/minimist": "^1.2.5",
    "@types/node": "24.1.0",
    "@vitest/coverage-v8": "catalog:",
    "@vitest/ui": "catalog:",
    dotenv: "catalog:",
    minimist: "^1.2.8",
    msw: "2.12.10",
    "openapi-types": "^12.1.3",
    orval: "8.33.0",
    tsup: "catalog:",
    tsx: "catalog:",
    typescript: "npm:@typescript/typescript6@^6.0.2",
    "typescript-config": "workspace:*",
    vite: "catalog:",
    vitest: "catalog:"
  },
  optionalDependencies: {
    "@esbuild/linux-x64": "0.28.2"
  },
  publishConfig: {
    access: "public"
  },
  volta: {
    extends: "../../package.json"
  },
  engines: {
    node: ">=22.x"
  },
  dependencies: {
    "js-sha1": "^0.7.0",
    "es-toolkit": "catalog:"
  }
};

// src/utils/headers.ts
function getConfigWithMetadata(apiConfig, functionName) {
  return {
    ...apiConfig,
    headers: mergeHeaders(apiConfig.headers, {
      "x-search-api-function": functionName,
      "x-search-api-version": package_default.version
    })
  };
}
function hasAuthorization(config) {
  return Boolean(config.jwtToken) || Boolean(config.headers?.["Authorization"]) || Boolean(config.getToken);
}

// src/utils/userAgent.ts
var isBrowserRuntime = () => typeof window !== "undefined" && typeof document !== "undefined";
var getRuntimeInfo = () => {
  const runtimeGlobal = globalThis;
  const bunVersion = runtimeGlobal.Bun?.version ?? runtimeGlobal.process?.versions?.bun;
  if (bunVersion) return `bun/${bunVersion}`;
  const denoVersion = runtimeGlobal.Deno?.version?.deno;
  if (denoVersion) return `deno/${denoVersion}`;
  const nodeVersion = runtimeGlobal.process?.versions?.node;
  if (nodeVersion) return `node/${nodeVersion}`;
  return "";
};
var getUserAgent = (applicationKey) => {
  const runtimeInfo = getRuntimeInfo();
  return `ev/${applicationKey} ${runtimeInfo}`.trim();
};

// src/utils/customFetchClient.ts
var wait = (durationMs) => {
  if (durationMs <= 0) {
    return Promise.resolve();
  }
  return new Promise((resolve) => {
    setTimeout(resolve, durationMs);
  });
};
var CustomFetchClient = class {
  constructor(options) {
    this.options = options;
  }
  options;
  async request(requestConfig, apiConfig) {
    const timeout = apiConfig?.timeout;
    const maxRetryCount = apiConfig?.maxRetryCount ?? this.options.defaultMaxRetryCount ?? 0;
    const retryDelayMs = apiConfig?.retryDelayMs ?? this.options.defaultRetryDelayMs ?? 250;
    const retryStatusCodes = new Set(apiConfig?.retryStatusCodes ?? this.options.defaultRetryStatusCodes ?? [429]);
    const headers = await this.getHeaders(requestConfig, apiConfig);
    if (!headers["x-application-key"]) {
      panic("x-application-key is required");
    }
    const url = this.getUrl(requestConfig, apiConfig);
    const resolvedRequestConfig = {
      ...requestConfig,
      headers,
      url
    };
    for (let attempt = 0; attempt <= maxRetryCount; attempt += 1) {
      const signalState = this.createRequestSignal(apiConfig?.signal ?? requestConfig.signal, timeout);
      try {
        const response = await fetch(url, {
          body: requestConfig.data === void 0 ? void 0 : JSON.stringify(requestConfig.data),
          headers,
          method: requestConfig.method,
          signal: signalState.signal
        });
        const data = await response.json().catch(() => void 0);
        if (!response.ok) {
          throw HttpError.fromResponse(response, {
            data,
            request: resolvedRequestConfig
          });
        }
        return data;
      } catch (error) {
        const normalizedError = this.getHttpError(error, {
          didTimeout: signalState.didTimeout(),
          requestConfig: resolvedRequestConfig,
          timeout
        });
        if (attempt >= maxRetryCount || !this.shouldRetry(normalizedError, retryStatusCodes)) {
          throw normalizedError;
        }
        await wait(retryDelayMs * 2 ** attempt);
      } finally {
        signalState.cleanup();
      }
    }
    throw new HttpError("The request failed before a response was returned.", {
      code: "ERR_NETWORK",
      request: resolvedRequestConfig
    });
  }
  createRequestSignal(signal, timeout) {
    if (!signal && !timeout) {
      return {
        cleanup: () => {
        },
        didTimeout: () => false,
        signal: void 0
      };
    }
    const controller = new AbortController();
    let didTimeout = false;
    let timeoutId;
    const abortFromSignal = () => {
      controller.abort(signal?.reason);
    };
    if (signal) {
      if (signal.aborted) {
        controller.abort(signal.reason);
      } else {
        signal.addEventListener("abort", abortFromSignal, { once: true });
      }
    }
    if (timeout && timeout > 0) {
      timeoutId = setTimeout(() => {
        didTimeout = true;
        controller.abort();
      }, timeout);
    }
    return {
      cleanup: () => {
        if (timeoutId) {
          clearTimeout(timeoutId);
        }
        if (signal) {
          signal.removeEventListener("abort", abortFromSignal);
        }
      },
      didTimeout: () => didTimeout,
      signal: controller.signal
    };
  }
  async getHeaders(requestConfig, apiConfig) {
    const jwtToken = apiConfig?.getToken ? await apiConfig.getToken({ requestConfig }) : apiConfig?.jwtToken;
    const baseHeaders = {};
    if (apiConfig?.applicationKey) {
      baseHeaders["x-application-key"] = apiConfig.applicationKey;
      if (!isBrowserRuntime()) {
        baseHeaders["user-agent"] = getUserAgent(apiConfig.applicationKey);
      }
    }
    if (apiConfig?.privateOfficePassword) {
      baseHeaders["X-PrivateOfficePassword"] = apiConfig.privateOfficePassword;
    }
    if (apiConfig?.basicAuth) {
      baseHeaders["Authorization"] = `Basic ${btoa(`${apiConfig.basicAuth.username}:${apiConfig.basicAuth.password}`)}`;
    }
    if (jwtToken) {
      baseHeaders["Authorization"] = `Bearer ${jwtToken}`;
    }
    return mergeHeaders(baseHeaders, apiConfig?.headers, requestConfig.headers);
  }
  getUrl(requestConfig, apiConfig) {
    const url = new URL(apiConfig?.baseURL ?? this.options.defaultBaseURL);
    const basePathname = url.pathname === "/" ? "" : url.pathname.replace(/\/$/, "");
    const requestPathname = requestConfig.url.startsWith("/") ? requestConfig.url : `/${requestConfig.url}`;
    const searchParams = new URLSearchParams();
    for (const [key, value] of Object.entries(requestConfig.params ?? {})) {
      if (value == null) {
        continue;
      }
      if (Array.isArray(value)) {
        for (const item of value) {
          if (item != null) {
            searchParams.append(key, String(item));
          }
        }
        continue;
      }
      searchParams.set(key, String(value));
    }
    url.pathname = `${basePathname}${requestPathname}`;
    url.search = searchParams.toString();
    return url.toString();
  }
  getHttpError(error, {
    didTimeout,
    requestConfig,
    timeout
  }) {
    if (isHttpError(error)) {
      return error;
    }
    if (didTimeout) {
      return new HttpError(`timeout of ${timeout}ms exceeded`, {
        cause: error,
        code: "ETIMEDOUT",
        request: requestConfig
      });
    }
    if (error instanceof DOMException && error.name === "AbortError") {
      return new HttpError("Request canceled", {
        cause: error,
        code: "ERR_CANCELED",
        request: requestConfig
      });
    }
    if (error instanceof Error) {
      return new HttpError(error.message, {
        cause: error,
        code: "ERR_NETWORK",
        request: requestConfig
      });
    }
    return new HttpError("An unknown network error occurred.", {
      cause: error,
      code: "ERR_NETWORK",
      request: requestConfig
    });
  }
  shouldRetry(error, retryStatusCodes) {
    return Boolean(error.response && retryStatusCodes.has(error.response.status));
  }
};

export {
  mergeHeaders,
  mergeApiConfig,
  getConfigWithMetadata,
  hasAuthorization,
  CustomFetchClient
};
